import { useState, useEffect, useMemo } from "react";
import { getPhotoUrl } from '@/utils/photoUrl';
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Send, 
  Target,
  Zap,
  Shield,
  Crosshair,
  Hand,
  Sparkles,
  Search,
  User,
  Check,
  Eye,
  Brain,
  MessageCircle,
  Activity,
  Move,
  Focus,
  Users,
  ChevronDown
} from "lucide-react";
import { useLanguage } from "@/i18n";

import { SkillSlider } from "@/components/evaluation/SkillSlider";
import { NeedsEvaluationBanner } from "@/components/evaluation/NeedsEvaluationBanner";

// Critérios para JOGADORES (não-levantadores)
const PLAYER_SKILLS = [
  { key: "ataque", label: "Ataque", icon: Zap, description: "Capacidade de finalização" },
  { key: "passe", label: "Passe", icon: Target, description: "Precisão nos passes" },
  { key: "defesa", label: "Defesa", icon: Shield, description: "Recuperação de bolas" },
  { key: "saque", label: "Saque", icon: Crosshair, description: "Eficiência no saque" },
  { key: "bloqueio", label: "Bloqueio", icon: Hand, description: "Bloqueio na rede" },
  { key: "toque", label: "Toque", icon: Target, description: "Qualidade do toque" },
  { key: "skill", label: "Skill", icon: Sparkles, description: "Habilidade geral" },
];

// Critérios específicos para LEVANTADORES
const SETTER_SKILLS = [
  { key: "toque_coordenacao", label: "Toque e Coordenação", icon: Hand, description: "Toque preciso, com controle, estabilidade e boa coordenação mão-olho" },
  { key: "controle_bola", label: "Controle da Bola", icon: Target, description: "Bola no tempo e local corretos para facilitar o ataque" },
  { key: "agilidade_agressividade", label: "Agilidade e Agressividade", icon: Move, description: "Mobilidade e iniciativa ofensiva, usando fintas e ataques" },
  { key: "compreensao_jogo", label: "Compreensão do Jogo", icon: Brain, description: "Entendimento do sistema e das situações de jogo" },
  { key: "visao_jogo", label: "Visão de Jogo", icon: Eye, description: "Leitura do adversário para criar vantagens" },
  { key: "tomada_decisao", label: "Tomada de Decisão", icon: Focus, description: "Escolhas eficientes de atacante, ritmo e jogada" },
  { key: "comunicacao", label: "Comunicação", icon: MessageCircle, description: "Comunicação clara para organizar o time" },
  { key: "atitude_mental", label: "Atitude Mental", icon: Activity, description: "Foco, competitividade, resiliência e vontade de evoluir" },
];

const POSITIONS = ["Levantador", "Ponteiro", "Oposto", "Central", "Líbero"];

export default function EvaluationForm({ evaluator, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const preSelectedPlayerId = searchParams.get('player');
  
  const [players, setPlayers] = useState([]);
  const [allowedPlayerIds, setAllowedPlayerIds] = useState(null);
  const [globalReferencePlayer, setGlobalReferencePlayer] = useState("");
  const [setterReferencePlayer, setSetterReferencePlayer] = useState("");
  const [referenceAverages, setReferenceAverages] = useState(null);
  const [setterReferenceAverages, setSetterReferenceAverages] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [profileType, setProfileType] = useState("jogador");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const isAdmin = evaluator?.role === 'admin';
  
  // Filtro de tipo de avaliação (Jogador ou Levantador)
  const [evaluationType, setEvaluationType] = useState("todos"); // "todos", "jogador", "levantador"
  
  // Skills para jogadores regulares
  const [skills, setSkills] = useState({
    ataque: 5,
    passe: 5,
    defesa: 5,
    saque: 5,
    bloqueio: 5,
    toque: 5,
    skill: 5,
  });
  
  // Skills específicos para levantadores
  const [setterSkills, setSetterSkills] = useState({
    toque_coordenacao: 5,
    controle_bola: 5,
    agilidade_agressividade: 5,
    compreensao_jogo: 5,
    visao_jogo: 5,
    tomada_decisao: 5,
    comunicacao: 5,
    atitude_mental: 5,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (evaluator?.role === 'admin') {
          const [playersRes, globalRefRes, setterRefRes] = await Promise.all([
            axios.get(`${API}/players`),
            axios.get(`${API}/global-reference`),
            axios.get(`${API}/setter-reference`)
          ]);
          setPlayers(playersRes.data);
          setGlobalReferencePlayer(globalRefRes.data?.reference_player_id || "");
          setSetterReferencePlayer(setterRefRes.data?.reference_setter_id || "");
          setAllowedPlayerIds(null);
        } else {
          const [playersRes, globalRefRes, setterRefRes] = await Promise.all([
            axios.get(`${API}/evaluator-permissions/${evaluator?.id}/players`),
            axios.get(`${API}/global-reference`),
            axios.get(`${API}/setter-reference`)
          ]);
          setPlayers(playersRes.data);
          setGlobalReferencePlayer(globalRefRes.data?.reference_player_id || "");
          setSetterReferencePlayer(setterRefRes.data?.reference_setter_id || "");
          setAllowedPlayerIds(null);
        }
      } catch (error) {
        toast.error(t('toasts.errorLoadingPlayers'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [evaluator]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pré-selecionar jogador da URL quando a lista carregar
  useEffect(() => {
    if (preSelectedPlayerId && players.length > 0 && !selectedPlayer) {
      const player = players.find(p => p.id === preSelectedPlayerId);
      if (player) {
        setSelectedPlayer(preSelectedPlayerId);
        setSelectedPosition(player.position || "");
        // Definir o tipo de perfil baseado na posição
        if (player.position === "Levantador") {
          setProfileType("levantador");
          setEvaluationType("levantador");
        } else {
          setProfileType("jogador");
          setEvaluationType("jogador");
        }
        setShowPlayerList(false);
        toast.success(`${player.first_name || player.name} ${t('toasts.playerPreselected')}`);
      }
    }
  }, [preSelectedPlayerId, players, selectedPlayer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carrega médias do jogador parâmetro global (para jogadores)
  useEffect(() => {
    const fetchReferenceAverages = async () => {
      if (!globalReferencePlayer) {
        setReferenceAverages(null);
        return;
      }
      try {
        const res = await axios.get(`${API}/players/${globalReferencePlayer}/averages`);
        setReferenceAverages(res.data?.averages || null);
      } catch (error) {
        setReferenceAverages(null);
      }
    };
    fetchReferenceAverages();
  }, [globalReferencePlayer]);

  // Carrega médias do levantador parâmetro (para levantadores)
  useEffect(() => {
    const fetchSetterReferenceAverages = async () => {
      if (!setterReferencePlayer) {
        setSetterReferenceAverages(null);
        return;
      }
      try {
        const res = await axios.get(`${API}/setters/${setterReferencePlayer}/averages`);
        setSetterReferenceAverages(res.data?.averages || null);
      } catch (error) {
        setSetterReferenceAverages(null);
      }
    };
    fetchSetterReferenceAverages();
  }, [setterReferencePlayer]);

  const handleSliderChange = (key, value) => {
    if (profileType === "levantador") {
      setSetterSkills({ ...setterSkills, [key]: value[0] });
    } else {
      setSkills({ ...skills, [key]: value[0] });
    }
  };

  const getPlayerName = (player) => {
    if (player.first_name) {
      return `${player.first_name} ${player.last_name || ''}`.trim();
    }
    return player.name || '';
  };

  const selectedPlayerObj = players.find(p => p.id === selectedPlayer);
  const referencePlayerObj = players.find(p => p.id === globalReferencePlayer);
  const setterReferencePlayerObj = players.find(p => p.id === setterReferencePlayer);
  const isSetter = selectedPlayerObj?.position === 'Levantador';

  const availablePlayers = allowedPlayerIds === null 
    ? players 
    : players.filter(p => allowedPlayerIds.includes(p.id));

  // Filtra jogadores baseado no tipo de avaliação selecionado
  const filteredByType = useMemo(() => {
    if (evaluationType === "levantador") {
      return availablePlayers.filter(p => p.position === 'Levantador');
    } else if (evaluationType === "jogador") {
      return availablePlayers.filter(p => p.position !== 'Levantador');
    }
    return availablePlayers;
  }, [availablePlayers, evaluationType]);

  // Filtra por busca de texto
  const filteredAndSortedPlayers = useMemo(() => {
    let filtered = [...filteredByType];
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      filtered = filtered.filter(player => {
        const name = getPlayerName(player).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const yankee = (player.yankee || "").toLowerCase();
        return name.includes(query) || yankee.includes(query);
      });
    }
    
    filtered.sort((a, b) => {
      const nameA = getPlayerName(a).toLowerCase();
      const nameB = getPlayerName(b).toLowerCase();
      return nameA.localeCompare(nameB, 'pt-BR');
    });
    
    return filtered;
  }, [filteredByType, searchQuery]);

  // Quando muda o tipo de avaliação, reseta seleções
  useEffect(() => {
    setSelectedPlayer("");
    setSearchQuery("");
    setShowSuggestions(false);
    setShowPlayerList(false);
    
    // Se selecionar "levantador", já define o profileType como levantador
    if (evaluationType === "levantador") {
      setProfileType("levantador");
    } else {
      setProfileType("jogador");
    }
  }, [evaluationType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlayer) {
      toast.error(t('toasts.selectPlayer'));
      return;
    }
    
    if (!evaluator?.id) {
      toast.error(t('toasts.sessionExpired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        player_id: selectedPlayer,
        evaluator_id: evaluator.id,
        profile_type: isSetter ? profileType : "jogador",
        evaluation_position: selectedPosition || selectedPlayerObj?.position || "Jogador",
        reference_player_id: profileType === "levantador" ? setterReferencePlayer : globalReferencePlayer || null
      };
      
      // Adiciona skills apropriados baseado no perfil
      if (isSetter && profileType === "levantador") {
        payload.setter_skills = setterSkills;
      } else {
        payload.skills = skills;
      }
      
      await axios.post(`${API}/evaluations`, payload);
      
      toast.success(t('toasts.evaluationSent'));
      
      // Reset form
      setSelectedPlayer("");
      setSelectedPosition("");
      setProfileType(evaluationType === "levantador" ? "levantador" : "jogador");
      setSearchQuery("");
      setShowPlayerList(false);
      setSkills({
        ataque: 5, passe: 5, defesa: 5, saque: 5, bloqueio: 5, toque: 5, skill: 5,
      });
      setSetterSkills({
        toque_coordenacao: 5, controle_bola: 5, agilidade_agressividade: 5,
        compreensao_jogo: 5, visao_jogo: 5, tomada_decisao: 5, comunicacao: 5, atitude_mental: 5,
      });
    } catch (error) {
      let errorMessage = t('toasts.errorSendingEvaluation');
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || String(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  // Calcula média baseado no tipo de perfil
  const overallAverage = (() => {
    const currentSkills = (isSetter && profileType === "levantador") ? setterSkills : skills;
    const values = Object.values(currentSkills);
    if (!values.length) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
  })();

  // Determina quais skills mostrar
  const currentSkillsList = (isSetter && profileType === "levantador") ? SETTER_SKILLS : PLAYER_SKILLS;
  const currentSkillsValues = (isSetter && profileType === "levantador") ? setterSkills : skills;
  const currentReferenceAverages = (isSetter && profileType === "levantador") ? setterReferenceAverages : referenceAverages;
  const currentReferencePlayer = (isSetter && profileType === "levantador") ? setterReferencePlayerObj : referencePlayerObj;

  // Contadores para os badges
  const countJogadores = availablePlayers.filter(p => p.position !== 'Levantador').length;
  const countLevantadores = availablePlayers.filter(p => p.position === 'Levantador').length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                Avaliação de Jogador
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-2xl content-with-nav">
        <Card className="bg-card border-border/50 shadow-xl">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Target className="h-6 w-6 text-primary" />
              Formulário de Avaliação
            </CardTitle>
            <CardDescription>
              Avaliador: <span className="text-foreground font-medium">{evaluator.name}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* STEP 1: Seleção do Tipo de Avaliação */}
              <div className="space-y-3">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  1. Tipo de Avaliação
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setEvaluationType("todos")}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      evaluationType === "todos"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50 text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="type-todos"
                  >
                    <Users className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm font-semibold">Todos</p>
                    <p className="text-xs opacity-70">{availablePlayers.length}</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setEvaluationType("jogador")}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      evaluationType === "jogador"
                        ? "border-blue-500 bg-blue-500/10 text-blue-500"
                        : "border-border hover:border-blue-500/50 text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="type-jogador"
                  >
                    <Zap className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm font-semibold">Jogador</p>
                    <p className="text-xs opacity-70">{countJogadores}</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setEvaluationType("levantador")}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      evaluationType === "levantador"
                        ? "border-yellow-500 bg-yellow-500/10 text-yellow-500"
                        : "border-border hover:border-yellow-500/50 text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="type-levantador"
                  >
                    <Hand className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm font-semibold">Levantador</p>
                    <p className="text-xs opacity-70">{countLevantadores}</p>
                  </button>
                </div>
              </div>

              {/* Banner: Jogadores que precisam de avaliação */}
              <NeedsEvaluationBanner
                evaluationType={evaluationType}
                onSelectPlayer={(player) => {
                  setSelectedPlayer(player.id);
                  setSelectedPosition(player.position || "");
                  setShowPlayerList(false);
                  setSearchQuery("");
                  if (player.position === "Levantador") {
                    setProfileType("levantador");
                  } else {
                    setProfileType("jogador");
                  }
                }}
              />

              {/* STEP 2: Seleção do Jogador - Lista em Cascata */}
              <div className="space-y-3">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  2. Selecionar {evaluationType === "levantador" ? "Levantador" : evaluationType === "jogador" ? "Jogador" : "Jogador/Levantador"}
                </Label>
                
                {/* Dropdown button para abrir a lista */}
                <button
                  type="button"
                  onClick={() => setShowPlayerList(!showPlayerList)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                    selectedPlayer 
                      ? "border-primary bg-primary/5" 
                      : "border-border hover:border-primary/50"
                  }`}
                  data-testid="player-dropdown-trigger"
                >
                  {selectedPlayerObj ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-muted overflow-hidden flex-shrink-0">
                        {selectedPlayerObj.photo_url ? (
                          <img 
                            src={getPhotoUrl(selectedPlayerObj.photo_url)} 
                            alt={getPlayerName(selectedPlayerObj)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold">{getPlayerName(selectedPlayerObj)}</p>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {selectedPlayerObj.position}
                          </Badge>
                          {selectedPlayerObj.yankee && (
                            <span className="text-xs text-muted-foreground">{selectedPlayerObj.yankee}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">
                      Clique para selecionar um {evaluationType === "levantador" ? "levantador" : "jogador"}...
                    </span>
                  )}
                  <ChevronDown className={`h-5 w-5 transition-transform ${showPlayerList ? "rotate-180" : ""}`} />
                </button>

                {/* Lista em Cascata (Dropdown expandido) */}
                {showPlayerList && (
                  <div className="border-2 border-border rounded-xl overflow-hidden bg-card shadow-xl">
                    {/* Campo de busca dentro do dropdown */}
                    <div className="p-3 border-b border-border bg-muted/30">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Buscar por nome..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 bg-background"
                          autoFocus
                          data-testid="player-search"
                        />
                      </div>
                    </div>
                    
                    {/* Lista de jogadores */}
                    <div className="max-h-80 overflow-y-auto">
                      {filteredAndSortedPlayers.length === 0 ? (
                        <div className="p-6 text-center text-muted-foreground">
                          <User className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p>Nenhum {evaluationType === "levantador" ? "levantador" : "jogador"} encontrado</p>
                          {searchQuery && (
                            <p className="text-xs mt-1">para "{searchQuery}"</p>
                          )}
                        </div>
                      ) : (
                        filteredAndSortedPlayers.map((player, index) => (
                          <button
                            key={player.id}
                            type="button"
                            onClick={() => {
                              setSelectedPlayer(player.id);
                              setSearchQuery("");
                              setShowPlayerList(false);
                              setSelectedPosition(player.position || "");
                              // Se for levantador, define o profile como levantador por padrão
                              if (player.position === 'Levantador') {
                                setProfileType("levantador");
                              } else {
                                setProfileType("jogador");
                              }
                            }}
                            className={`w-full flex items-center gap-3 p-3 hover:bg-primary/10 transition-colors border-b border-border/50 last:border-0 ${
                              selectedPlayer === player.id ? "bg-primary/20" : ""
                            } ${index % 2 === 0 ? "bg-muted/20" : ""}`}
                            data-testid={`player-option-${player.id}`}
                          >
                            <div className="w-10 h-10 rounded-full bg-muted overflow-hidden flex-shrink-0">
                              {player.photo_url ? (
                                <img 
                                  src={getPhotoUrl(player.photo_url)} 
                                  alt={getPlayerName(player)}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <User className="h-5 w-5 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <p className="font-medium truncate">
                                {player.yankee && (
                                  <span className="font-mono text-primary mr-2">{player.yankee}</span>
                                )}
                                {getPlayerName(player)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {player.position}
                                {player.position2 && ` • ${player.position2}`}
                              </p>
                            </div>
                            {selectedPlayer === player.id && (
                              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                    
                    {/* Footer com contador */}
                    <div className="p-2 border-t border-border bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground">
                        {filteredAndSortedPlayers.length} {filteredAndSortedPlayers.length === 1 ? "encontrado" : "encontrados"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Position Selection - Mostra apenas as posições atribuídas ao jogador */}
              {selectedPlayer && selectedPlayerObj && (
                <div className="space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    3. Posição para Avaliação
                  </Label>
                  {/* Se o jogador só tem uma posição, mostra como texto fixo */}
                  {!selectedPlayerObj.position2 ? (
                    <div className="p-3 rounded-lg bg-input/50 border border-border">
                      <p className="font-medium">{selectedPlayerObj.position}</p>
                      <p className="text-xs text-muted-foreground">Posição única do jogador</p>
                    </div>
                  ) : (
                    <Select 
                      value={selectedPosition} 
                      onValueChange={setSelectedPosition}
                    >
                      <SelectTrigger className="bg-input/50 border-border">
                        <SelectValue placeholder="Escolha a posição..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={selectedPlayerObj.position}>
                          {selectedPlayerObj.position}
                          <span className="ml-2 text-xs text-muted-foreground">(posição principal)</span>
                        </SelectItem>
                        <SelectItem value={selectedPlayerObj.position2}>
                          {selectedPlayerObj.position2}
                          <span className="ml-2 text-xs text-muted-foreground">(posição secundária)</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {/* Profile Type Selection for Setters */}
              {isSetter && (
                <div className="space-y-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                  <Label className="text-xs font-bold uppercase tracking-widest text-yellow-600">
                    4. Perfil de Avaliação (Levantador)
                  </Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Este jogador é Levantador e possui <strong>2 perfis</strong> de avaliação com critérios diferentes.
                  </p>
                  <Select 
                    value={profileType} 
                    onValueChange={setProfileType}
                  >
                    <SelectTrigger className="bg-input/50 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="levantador">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">🎯 Perfil Levantador</span>
                          <span className="text-xs text-muted-foreground">- 8 critérios específicos</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="jogador">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">⚡ Perfil Jogador</span>
                          <span className="text-xs text-muted-foreground">- 7 critérios gerais</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Reference player summary */}
              {currentReferencePlayer && selectedPlayer && (
                <div className="p-3 rounded-xl bg-secondary/20 border border-dashed border-border/60 flex flex-col sm:flex-row gap-3 items-center">
                  <div className="w-14 h-14 rounded-full overflow-hidden border border-primary/40 flex-shrink-0 bg-muted flex items-center justify-center">
                    {currentReferencePlayer.photo_url ? (
                      <img
                        src={getPhotoUrl(currentReferencePlayer.photo_url)}
                        alt={getPlayerName(currentReferencePlayer)}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-[10px] text-muted-foreground text-center px-1">Sem foto</div>
                    )}
                  </div>
                  <div className="flex-1 text-center sm:text-left space-y-0.5">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                      {profileType === "levantador" ? "Levantador Parâmetro" : "Jogador Parâmetro"}
                    </p>
                    <p className="text-sm font-semibold">{getPlayerName(currentReferencePlayer)}</p>
                    <p className="text-xs text-muted-foreground">
                      {currentReferencePlayer.position}
                    </p>
                  </div>
                </div>
              )}

              {/* Skills Sliders */}
              {selectedPlayer && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        {profileType === "levantador" ? "Critérios de Levantador (0-10)" : "Avaliação das Habilidades (0-10)"}
                      </Label>
                      {currentReferencePlayer && (
                        <>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            Tomando como base o {profileType === "levantador" ? "levantador" : "jogador"} <span className="font-semibold">
                              {getPlayerName(currentReferencePlayer)}
                            </span>, avalie os critérios abaixo.
                          </p>
                          <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-3 h-1 rounded-full bg-primary" />
                              <span>{profileType === "levantador" ? "Levantador avaliado" : "Jogador avaliado"}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="inline-block w-3 h-1 rounded-full bg-red-500/70" />
                              <span>{profileType === "levantador" ? "Levantador parâmetro" : "Jogador parâmetro"}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Média Geral</p>
                      <p className={`text-2xl font-black ${getScoreColor(overallAverage)}`} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {overallAverage.toFixed(1)}
                      </p>
                    </div>
                  </div>
                  
                  {currentSkillsList.map((skill, index) => (
                    <SkillSlider
                      key={skill.key}
                      skill={skill}
                      value={currentSkillsValues[skill.key]}
                      referenceValue={currentReferenceAverages?.[skill.key]}
                      referencePlayerName={currentReferencePlayer ? getPlayerName(currentReferencePlayer) : null}
                      onChange={(value) => handleSliderChange(skill.key, value)}
                      index={index}
                    />
                  ))}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-lg py-6"
                disabled={!selectedPlayer || isSubmitting}
                data-testid="submit-evaluation"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="h-5 w-5" />
                    Enviar Avaliação
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-sm border-t border-border py-2 px-4 md:hidden z-50">
        <div className="flex justify-around items-center">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 h-auto py-2">
            <ArrowLeft className="h-5 w-5" />
            <span className="text-[10px]">Voltar</span>
          </Button>
        </div>
      </nav>
    </div>
  );
}
